import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { customerSectionPath } from '../../lib/navigation.js';
import SectionCard from '../../components/SectionCard.jsx';
import { deploymentAccountId } from '../../../../shared/customer-account.mjs';
import { deployFailureText } from '../../lib/deploy-failure.js';

/**
 * Step 3: Install (ENTRAID-350, Hugo Andrade 2026-10-26: "remove every click that carries no decision").
 *
 * One install screen instead of a "Start collection" form: as soon as AWS access is confirmed the install starts on
 * its own (POST /deploy is idempotent, so a refresh or a second tab never starts a second one), and the screen shows
 * the real stages from GET /deploy-status as they happen. A failure says what happened and what to do; Try again is
 * offered only when trying again can succeed.
 */

export const INSTALL_POLL_MS = 5000;
// How long the screen waits for an install another tab is still starting before it offers Try again.
export const INSTALL_START_WAIT_MS = 2 * 60 * 1000;
// How long it waits for the reports and Watch after the collector is installed; after that Done opens anyway (the
// first collection does not depend on them, and IPC Global finishes them).
export const INSTALL_EXTRAS_WAIT_MS = 10 * 60 * 1000;

const STAGES = [
  { id: 'entra', label: 'Connect Entra ID' },
  { id: 'aws', label: 'AWS access' },
  { id: 'collector', label: 'Installing the collector in your AWS account' },
  { id: 'extras', label: 'Installing reports and Watch' },
  { id: 'firstRun', label: 'First data collection' },
];

const SUPPORT = 'support@ipc-global.com';

export function formatAccountId(id) {
  return String(id ?? '').replace(/^(\d{4})(\d{4})(\d{4})$/, '$1 $2 $3');
}

/** A starting progress view before deploy-status answered: the two steps the customer already did are done. */
function initialProgress(customer) {
  return {
    accountId: deploymentAccountId(customer),
    stages: { entra: 'done', aws: 'done', collector: customer.status === 'deploying' ? 'running' : 'pending', extras: 'pending', firstRun: 'pending' },
    detail: { resources: null },
    percent: 20,
    settled: false,
  };
}

function stageLine(id, state, progress) {
  const account = progress?.accountId ? formatAccountId(progress.accountId) : null;
  switch (id) {
    case 'entra':
      return 'Microsoft Entra ID is connected.';
    case 'aws':
      return account ? `Detected in account ${account}.` : 'The AWS stack is created.';
    case 'collector': {
      if (state === 'done') return 'Installed.';
      if (state === 'failed') return 'Not installed.';
      if (state === 'running') {
        const r = progress?.detail?.resources;
        return r && r.total > 0 ? `Creating resources: ${r.complete} of ${r.total} ready.` : 'Creating the resources in your AWS account.';
      }
      return 'Starts in a moment.';
    }
    case 'extras':
      if (state === 'done') return 'Installed.';
      if (state === 'failed') return 'IPC Global finishes this for you. Data collection is not affected.';
      if (state === 'running') return 'Adding the reports and Watch to your collector.';
      return 'After the collector.';
    case 'firstRun':
      if (state === 'done') return 'Finished.';
      if (state === 'running') return 'Started. Results appear in a few minutes.';
      return 'After the collector.';
    default:
      return '';
  }
}

// The stage the customer is waiting for now, used for the screen reader announcement (once per change, not per poll).
function currentStage(progress) {
  const stages = progress?.stages ?? {};
  for (const s of STAGES) {
    const state = stages[s.id];
    if (state === 'failed' && s.id === 'collector') return { id: s.id, state };
    if (state !== 'done' && state !== 'skipped' && state !== 'failed') return { id: s.id, state };
  }
  return { id: 'all', state: 'done' };
}

function StageIcon({ state }) {
  const map = {
    done: { text: '✓', cls: 'bg-ipc-bar-pass text-white', label: 'Done' },
    running: { text: '', cls: 'bg-ipc-light', label: 'In progress' },
    failed: { text: '!', cls: 'bg-ipc-bar-fail text-white', label: 'Failed' },
    skipped: { text: '-', cls: 'bg-ipc-track text-ipc-muted', label: 'Not included' },
    pending: { text: '', cls: 'bg-ipc-track', label: 'Waiting' },
  };
  const m = map[state] ?? map.pending;
  return (
    <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center text-xs font-bold ${m.cls} ${state === 'running' ? 'animate-pulse' : ''}`}>
      <span aria-hidden="true">{m.text}</span>
      <span className="sr-only">{m.label}</span>
    </span>
  );
}

/**
 * Turns a POST /deploy refusal into what the customer can do about it. `retry` is offered only when trying again can
 * succeed: an account that IPC Global must approve, or a name conflict, will not be fixed by trying again.
 */
function startFailure(e) {
  const details = e?.body?.details ?? {};
  if (e?.status === 409 && details.tenantVerified === false) {
    return { kind: 'reconnect', title: 'Microsoft Entra ID must be connected again', message: e.message, retry: false };
  }
  if (e?.status === 409 && typeof details.reason === 'string' && details.reason.startsWith('aws-account-')) {
    return { kind: 'support', title: 'IPC Global must approve this AWS account', message: e.message, retry: false, account: true };
  }
  if (e?.status === 409 && details.reason === 'stack-name-conflict') {
    return { kind: 'support', title: 'The collector cannot be installed in this AWS account yet', message: e.message, retry: false };
  }
  if (e?.status === 429) {
    // The backend says when trying again can succeed (review L4).
    return { kind: 'retry', title: 'The install can start again shortly', message: e.message, retry: true };
  }
  if (e?.status === 409 || e?.status === 400 || e?.status === 403) {
    return { kind: 'support', title: 'The install could not start', message: e.message, retry: false };
  }
  return {
    kind: 'retry',
    title: 'The install could not start',
    message: `${e?.message || 'The request failed.'} Nothing was installed. Try again; if it fails again, contact ${SUPPORT}.`,
    retry: true,
  };
}

export default function InstallStep({ customer, onDone, onReconnect, onBackToTrust }) {
  const [progress, setProgress] = useState(() => initialProgress(customer));
  const [failure, setFailure] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const startedRef = useRef(false);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);
  const startingSinceRef = useRef(null);
  const deployedSinceRef = useRef(null);
  const lastStageRef = useRef('');
  const awsRegion = customer.awsRegion || 'us-east-1';

  const stop = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    return () => { stoppedRef.current = true; stop(); };
  }, [stop]);

  const finish = useCallback((p) => {
    stop();
    onDone({ ...customer, status: 'deployed', firstRunDone: p?.stages?.firstRun === 'done' });
  }, [customer, onDone, stop]);

  const applyProgress = useCallback((next) => {
    if (!next) return;
    // The bar never moves backwards (a resource count can lag behind the stage).
    setProgress((prev) => ({ ...next, percent: Math.max(prev?.percent ?? 0, next.percent ?? 0) }));
    const cur = currentStage(next);
    const key = `${cur.id}:${cur.state}`;
    if (key !== lastStageRef.current) {
      lastStageRef.current = key;
      const label = STAGES.find((s) => s.id === cur.id)?.label;
      if (cur.id === 'all') setAnnouncement('The install is complete.');
      else if (label && cur.state === 'running') setAnnouncement(`${label}.`);
    }
  }, []);

  const poll = useCallback(async () => {
    if (stoppedRef.current) return;
    let s = null;
    try {
      s = await api.getDeployStatus(customer.customerId);
    } catch {
      // A transient error must not end the install view: check again.
    }
    if (stoppedRef.current) return;
    if (s) {
      applyProgress(s.progress);
      if (s.status === 'deploy-failed') {
        stop();
        setFailure({
          kind: 'retry',
          title: 'The collector could not be installed in your AWS account',
          message: 'AWS CloudFormation stopped the install.',
          reason: deployFailureText(s.deployFailureReason),
          retry: true,
        });
        setAnnouncement('The install failed.');
        return;
      }
      if (s.status === 'deployed' || s.status === 'active') {
        deployedSinceRef.current = deployedSinceRef.current ?? Date.now();
        if (s.progress?.settled || Date.now() - deployedSinceRef.current >= INSTALL_EXTRAS_WAIT_MS) {
          finish(s.progress);
          return;
        }
      } else if (s.status !== 'deploying') {
        // Another tab is still starting the install (the backend answered already-starting).
        startingSinceRef.current = startingSinceRef.current ?? Date.now();
        if (Date.now() - startingSinceRef.current >= INSTALL_START_WAIT_MS) {
          stop();
          setFailure({ kind: 'retry', title: 'The install did not start', message: `Nothing was installed. Try again; if it fails again, contact ${SUPPORT}.`, retry: true });
          return;
        }
      }
    }
    timerRef.current = setTimeout(poll, INSTALL_POLL_MS);
  }, [applyProgress, customer.customerId, finish, stop]);

  const start = useCallback(async () => {
    setFailure(null);
    startingSinceRef.current = null;
    setProgress((p) => ({ ...p, stages: { ...p.stages, collector: 'running' } }));
    setAnnouncement('Installing the collector in your AWS account.');
    try {
      // intent 'install' (review M1): an installed customer answers already-installed, so a stale tab or Back and
      // Forward never redeploys; the poll below then shows the finished install.
      await api.deploy(customer.customerId, { awsRegion, intent: 'install' });
    } catch (e) {
      const f = startFailure(e);
      setFailure(f);
      setProgress((p) => ({ ...p, stages: { ...p.stages, collector: 'failed' } }));
      setAnnouncement(f.title);
      return;
    }
    poll();
  }, [awsRegion, customer.customerId, poll]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!customer.trustRoleArn) return;
    if (customer.status === 'deploying') { poll(); return; }
    if (customer.status === 'deploy-failed') {
      // A failed install is shown as failed, with its reason, and is never restarted without the customer.
      setProgress((p) => ({ ...p, stages: { ...p.stages, collector: 'failed' } }));
      setFailure({
        kind: 'retry',
        title: 'The collector could not be installed in your AWS account',
        message: 'AWS CloudFormation stopped the last install.',
        reason: deployFailureText(customer.deployFailureReason),
        retry: true,
      });
      return;
    }
    start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!customer.trustRoleArn) {
    return (
      <SectionCard as="div" bodyClassName="space-y-4">
        <h2>Step 3: Install onGuard</h2>
        <div className="border border-ipc-border px-4 py-3 text-sm text-ipc-ink space-y-2" style={{ borderLeft: '4px solid var(--warn)' }} role="alert">
          <p>AWS access is not set up yet, so nothing can be installed.</p>
          {onBackToTrust && <button type="button" className="btn-primary" onClick={onBackToTrust}>Set up AWS access</button>}
        </div>
      </SectionCard>
    );
  }

  const stages = progress?.stages ?? {};
  const failed = Boolean(failure);
  const percent = failed ? Math.max(progress?.percent ?? 0, 20) : (progress?.percent ?? 0);
  const mailto = `mailto:${SUPPORT}?subject=${encodeURIComponent(`onGuard install: ${customer.customerId}`)}`;

  return (
    <SectionCard as="div" bodyClassName="space-y-5">
      <div>
        <h2>Step 3: Install onGuard</h2>
        <p className="text-sm text-ipc-muted mt-1">
          onGuard installs its collector in your AWS account. Your Entra ID data stays in that account.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-xs text-ipc-muted">
          <span>{failed ? 'Install stopped' : stages.collector === 'done' ? 'Finishing the install' : 'Installing'}</span>
          <span>{percent}%</span>
        </div>
        <div
          className="h-2 overflow-hidden bg-ipc-track"
          role="progressbar"
          aria-label="Install progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className={`h-full transition-all duration-700 ${failed ? 'bg-ipc-bar-fail' : 'bg-ipc-light'}`} style={{ width: `${percent}%` }} />
        </div>
      </div>

      <ol className="space-y-3 text-sm" aria-label="Install steps">
        {STAGES.map((s) => {
          const state = stages[s.id] ?? 'pending';
          return (
            <li key={s.id} className="flex items-start gap-3" data-stage={s.id} data-state={state}>
              <StageIcon state={state} />
              <div className="min-w-0">
                <p className={state === 'pending' ? 'text-ipc-muted' : 'font-medium text-ipc-ink'}>{s.label}</p>
                <p className="text-xs text-ipc-muted">{stageLine(s.id, state, progress)}</p>
              </div>
            </li>
          );
        })}
      </ol>

      {!failed && stages.collector !== 'done' && (
        <p className="text-xs text-ipc-muted">This usually takes two to three minutes. Keep this page open: it finishes the install.</p>
      )}

      {failure && (
        <div className="border border-ipc-border px-4 py-3 space-y-2" style={{ borderLeft: '4px solid var(--bad)' }} role="alert" data-testid="install-failure">
          <p className="text-sm font-medium text-ipc-ink">{failure.title}</p>
          <p className="text-sm text-ipc-ink">{failure.message}</p>
          {failure.reason && <p className="text-sm text-ipc-ink">{failure.reason}</p>}
          {failure.kind === 'retry' && (
            <p className="text-xs text-ipc-muted">Try again. If it fails again, write to <a className="underline" href={mailto}>{SUPPORT}</a>: IPC Global sees the full details.</p>
          )}
          {failure.kind === 'support' && (
            <p className="text-xs text-ipc-muted">
              {failure.account
                ? <>Go back to AWS access to request the approval, or write to <a className="underline" href={mailto}>{SUPPORT}</a>.</>
                : <>Write to <a className="underline" href={mailto}>{SUPPORT}</a> and IPC Global sorts it out with you.</>}
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {failure.retry && <button type="button" className="btn-primary" onClick={start}>Try again</button>}
            {failure.kind === 'reconnect' && onReconnect && (
              <button type="button" className="btn-primary" onClick={onReconnect}>Reconnect Microsoft Entra ID</button>
            )}
            {failure.account && onBackToTrust && (
              <button type="button" className="btn-primary" onClick={onBackToTrust}>Back to AWS access</button>
            )}
          </div>
        </div>
      )}

      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
    </SectionCard>
  );
}

/* ---------- Step 4: Done ---------- */

export function DoneStep({ customer }) {
  const account = deploymentAccountId(customer);
  const firstRunDone = customer.firstRunDone === true;
  return (
    <SectionCard as="div" title="You're all set" bodyClassName="space-y-4">
      <p className="text-sm text-ipc-muted">
        onGuard is installed in your AWS account
        {account && <> (<code>{formatAccountId(account)}</code>)</>}.
      </p>
      <div className="border border-ipc-border px-4 py-3" style={{ borderLeft: '4px solid var(--good)' }} data-testid="first-run-notice">
        <p className="text-sm text-ipc-ink font-medium">
          {firstRunDone ? 'Your first data collection has finished' : 'Your first data collection is running now'}
        </p>
        <p className="text-xs text-ipc-muted mt-1">
          {firstRunDone
            ? 'After this, data is collected automatically every day.'
            : 'Results typically appear within a few minutes. After this, data is collected automatically every day.'}
        </p>
      </div>
      <div>
        <Link className="btn-primary" to={customerSectionPath(customer.customerId, 'overview')}>Go to Overview</Link>
      </div>
    </SectionCard>
  );
}
